import { find, get, isEmpty } from "lodash";
import omitDeep from "omit-deep-lodash";
import React, { useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import useSubscription from "shared/services/subscriptions";
import { names } from "shared/services/subscriptions/constants";
import graphql from "utils/api/graphql";
import { UPDATE_PROJECT } from "utils/api/graphql/mutations/projects";
import { CREATE_QUALIFICATION } from "utils/api/graphql/mutations/qualifications";
import { TRACERS } from "utils/api/graphql/queries/tracers";
import { CONTACT_POINT_SYSTEM, TRACER_TYPES } from "utils/constants";
import { getPaginationHistory } from "utils/helpers/pagination";
import View from "./View";

const ContactCard = ({
  open,
  project,
  setOpen,
  contactId,
  matchStatusesWithProcess,
  tracers,
  setTracers,
}) => {
  const [projects, setProjects] = useState({ count: 0, data: [] });
  const [loading, setLoading] = useState(true);
  const [isAssigning, setIsAssigning] = useState({});

  useQuery(TRACERS, {
    variables: { where: { type: TRACER_TYPES.MANUAL } },
    onCompleted: ({ tracers: { data } }) => {
      setTracers(omitDeep(data, "__typename"));
      setLoading(false);
    },
  });

  const [addQualification] = useMutation(CREATE_QUALIFICATION, {
    refetchQueries: [
      {
        query: graphql.queries.QUALIFIERS,
        awaitRefetchQueries: true,
        variables: {
          withDeleted: true,
          where: {
            qualifications: {
              contact: { id: contactId },
            },
          },
        },
      },
    ],
  });
  const filter = {
    withSubscriptions: false,
    withContract: true,
    withProcess: false,
    withProcessStatuses: false,
    withTracers: false,
    withFlows: false,
    exclude: [
      "subscriptions",
      "process",
      "processStatuses",
      "flows",
      "tracers",
      "possibleStatuses",
      "conditions",
      "actions",
      "actionsConditions",
    ],
  };

  const [getContactProjects] = useLazyQuery(graphql.queries.PROJECTS_LIST, {
    fetchPolicy: "no-cache",
  });
  const onClose = () => {
    setOpen(false);
  };
  const { data: qualifiers } = useQuery(graphql.queries.QUALIFIERS);
  const { data: currentQualifier } = useQuery(graphql.queries.QUALIFIER, {
    fetchPolicy: "no-cache",
    variables: {
      withDeleted: true,
      where: {
        AND: [
          {
            qualifications: {
              last: true,
            },
          },
          {
            qualifications: {
              contact: { id: contactId },
            },
          },
        ],
      },
    },
  });
  const { data } = useQuery(graphql.queries.CONTACT, {
    variables: { where: { id: contactId } },
    onCompleted: ({ contact }) => {
      if (!contact || !contact.telecoms) {
        setProjects({ count: 0, data: [] });
        setLoading(false);
        return;
      }

      const email = get(
        find(contact.telecoms, { system: CONTACT_POINT_SYSTEM.EMAIL }),
        "value",
      );

      if (isEmpty(email)) {
        setProjects({ count: 0, data: [] });
        setLoading(false);
        return;
      }

      getContactProjects({
        variables: {
          where: {
            contact: { telecoms: { value: email } },
          },
          ...filter,
        },
        onCompleted: ({ projectsList }) => {
          setLoading(false);
          setProjects(omitDeep(projectsList, "__typename"));
        },
      });
    },
  });
  useSubscription({ query: names.QUALIFIERS, variables: {} });

  const [updateProject] = useMutation(UPDATE_PROJECT, {
    refetchQueries: [
      {
        query: graphql.queries.PROJECTS_LIST,
        awaitRefetchQueries: true,
        variables: {
          ...getPaginationHistory("projectsList"),
          where: { visible: true },
          withSubscriptions: false,
          withContract: false,
          withProcess: false,
          withProcessStatuses: false,
          withTracers: false,
          withFlows: false,
          exclude: [
            "subscriptions",
            "contract",
            "process",
            "processStatuses",
            "flows",
            "tracers",
            "possibleStatuses",
            "conditions",
            "actions",
            "actionsConditions",
          ],
        },
      },
    ],
  });
  const [active, setActive] = useState("INFORMATION");
  const onAssign = ({ projectId, authorId }) => {
    setIsAssigning({ [projectId]: true });
    updateProject({
      variables: {
        where: { id: projectId },
        data: {
          author: { id: authorId },
          fields: { ...get(project, "fields", {}), isManualAssign: true },
        },
      },
      onCompleted: () => setTimeout(() => setIsAssigning({}), 1000),
    });
  };

  return (
    <View
      key={contactId}
      drawer={open}
      loading={loading}
      onClose={onClose}
      project={project}
      active={active}
      setActive={setActive}
      contact={omitDeep(get(data, "contact"), "__typename")}
      projects={projects}
      onAssign={onAssign}
      isAssigning={isAssigning}
      matchStatusesWithProcess={matchStatusesWithProcess}
      tracers={tracers}
      qualifiers={get(qualifiers, "qualifiers.data", [])}
      addQualification={addQualification}
      currentQualifier={currentQualifier}
    />
  );
};

export default React.memo(ContactCard);
